import type { FormCapability, FormScope } from "@embedpdf/plugin-form";
import { PdfErrorCode, Task, TaskStage } from "@embedpdf/models";
import type { PdfErrorReason, PdfTask } from "@embedpdf/models";

interface FormEditQueue {
  tail: Promise<void>;
  error?: unknown;
}

export class PdfFormEditError extends Error {
  constructor() {
    super("Your latest PDF edit could not be saved. Reopen the document before downloading.");
  }
}

/** Keep asynchronous native edits ordered independently for each document. */
export function createPdfFormEditQueue() {
  const documents = new Map<string, FormEditQueue>();

  function enqueue(documentId: string | undefined, start: () => PdfTask<boolean>) {
    if (!documentId) return start();
    let state = documents.get(documentId);
    if (!state) {
      state = { tail: Promise.resolve() };
      documents.set(documentId, state);
    }
    const current = state;
    const result = new Task<boolean, PdfErrorReason>();
    let active: PdfTask<boolean> | undefined;
    result.wait(() => {}, error => {
      if (error.type === "abort") active?.abort(error.reason);
    });
    current.tail = current.tail.then(async () => {
      if (result.state.stage === TaskStage.Aborted) {
        current.error = new Error("PDF field update was cancelled");
        return;
      }
      try {
        active = start();
        const applied = await active.toPromise();
        if (!applied) throw new Error("PDF field update did not complete");
        result.resolve(applied);
      } catch (error) {
        current.error = error;
        result.reject({ code: PdfErrorCode.Unknown, message: "PDF field update failed" });
      }
    });
    return result;
  }


  return {
    enqueue,
    async wait(documentId: string) {
      const state = documents.get(documentId);
      if (!state) return;
      let tail: Promise<void>;
      do {
        tail = state.tail;
        await tail;
      } while (tail !== state.tail);
      if (state.error) throw new PdfFormEditError();
    },
    forget(documentId: string) { documents.delete(documentId); },
  };
}

/** Adapt public form operations before the viewer mounts its native widgets. */
export function createPdfFormEditBarrier(base: Readonly<FormCapability>) {
  const queue = createPdfFormEditQueue();
  const capability: FormCapability = {
    ...base,
    setFormFieldValues: (pageIndex, annotation, newField, documentId) =>
      queue.enqueue(documentId, () => base.setFormFieldValues(pageIndex, annotation, newField, documentId)),
    setFormValues: (values, documentId) =>
      queue.enqueue(documentId, () => base.setFormValues(values, documentId)),
    forDocument(documentId) {
      const scope = base.forDocument(documentId);
      const wrapped: FormScope = {
        ...scope,
        setFormFieldValues: (pageIndex, annotation, newField) =>
          queue.enqueue(documentId, () => scope.setFormFieldValues(pageIndex, annotation, newField)),
        setFormValues: values => queue.enqueue(documentId, () => scope.setFormValues(values)),
      };
      return wrapped;
    },
  };

  return { capability: Object.freeze(capability), wait: queue.wait, forget: queue.forget };
}
